import { api } from './axios';
import { authService } from './auth';
import type { Demanda } from '../models';
import { demandasCache } from './demandasCache';

/**
 * Interface para filtros de demandas
 * Compatível com os parâmetros esperados pelo DemandaController.php
 */
export interface DemandaFilters {
  buscar?: string;       // Busca geral (título, número, processo, demandante)
  status?: string;       // Status da demanda (ex: "EMATRIBUICAO", "EMPROGRESSO")
  prioridade?: string;   // Prioridade (ex: "Baixa", "Media", "Alta", "Urgente")
  processo_fk?: string | number; // ID do processo
}

class ReceivedDemandsService {
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  /**
   * Constrói query string a partir dos filtros
   */
  private buildQueryString(filters?: DemandaFilters): string {
    if (!filters) return '';
    
    const params = new URLSearchParams();
    
    // Adiciona apenas filtros com valores válidos (não vazios)
    if (filters.buscar && filters.buscar.trim()) {
      params.append('buscar', filters.buscar.trim());
    }
    if (filters.status && filters.status.trim()) {
      params.append('status', filters.status);
    }
    if (filters.prioridade && filters.prioridade.trim()) {
      params.append('prioridade', filters.prioridade);
    }
    if (filters.processo_fk && String(filters.processo_fk).trim()) {
      params.append('processo_fk', String(filters.processo_fk));
    }
    
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Gera uma chave de cache única baseada nos filtros
   */
  private getCacheKeyWithFilters(baseKey: string, filters?: DemandaFilters): string {
    if (!filters) return baseKey;
    
    const filterParts: string[] = [];
    if (filters.buscar) filterParts.push(`b:${filters.buscar}`);
    if (filters.status) filterParts.push(`s:${filters.status}`);
    if (filters.prioridade) filterParts.push(`p:${filters.prioridade}`);
    if (filters.processo_fk) filterParts.push(`pf:${filters.processo_fk}`);
    
    return filterParts.length > 0 ? `${baseKey}:${filterParts.join(',')}` : baseKey;
  }

  /**
   * Método privado para buscar com cache
   */
  private async fetchWithCache(
    endpoint: string, 
    cacheKey: string, 
    forceRefresh: boolean = false,
    filters?: DemandaFilters
  ): Promise<Demanda[]> {
    // Gerar chave de cache considerando os filtros
    const fullCacheKey = this.getCacheKeyWithFilters(cacheKey, filters);
    
    // Verificar cache primeiro (se não for refresh forçado)
    if (!forceRefresh) {
      const cached = demandasCache.get(fullCacheKey);
      if (cached) {
        return cached;
      }
    }

    // Garantir que Sanctum esteja inicializado
    await authService.ensureSanctumInitialized();
    
    // Construir URL com filtros
    const queryString = this.buildQueryString(filters);
    const fullEndpoint = `${endpoint}${queryString}`;
    
    const response = await api.get<Demanda[]>(fullEndpoint);
    const data = response.data;
    
    // Armazenar no cache
    demandasCache.set(fullCacheKey, data, this.CACHE_TTL);
    
    return data;
  }

  // Buscar todas as demandas
  async getDemandas(forceRefresh: boolean = false, filters?: DemandaFilters): Promise<Demanda[]> {
    return this.fetchWithCache('/demandas', 'all', forceRefresh, filters);
  }

  // Buscar demandas recebidas no setor logado
  async getDemandasRecebidas(forceRefresh: boolean = false, filters?: DemandaFilters): Promise<Demanda[]> {
    return this.fetchWithCache('/demandas/recebidas', 'received', forceRefresh, filters);
  }

  // Buscar demandas enviadas pelo usuário logado
  async getDemandasEnviadas(forceRefresh: boolean = false, filters?: DemandaFilters): Promise<Demanda[]> {
    return this.fetchWithCache('/demandas/enviadas', 'sent', forceRefresh, filters);
  }

  // Buscar demandas observáveis pelo usuário logado
  async getDemandasObservaveis(forceRefresh: boolean = false, filters?: DemandaFilters): Promise<Demanda[]> {
    return this.fetchWithCache('/demandas/observaveis', 'watching', forceRefresh, filters);
  }

  // Buscar demandas em atribuição no setor logado
  async getDemandasEmAtribuicao(forceRefresh: boolean = false, filters?: DemandaFilters): Promise<Demanda[]> {
    return this.fetchWithCache('/demandas/emAtribuicao', 'toAssign', forceRefresh, filters);
  }

  // Buscar demandas aguardando autorização do usuário logado
  async getMinhasAutorizacoes(forceRefresh: boolean = false): Promise<any[]> {
    if (forceRefresh) {
      demandasCache.invalidate('authorization');
    }

    if (!forceRefresh) {
      const cached = demandasCache.get('authorization');
      if (cached) {
        return cached;
      }
    }

    await authService.ensureSanctumInitialized();
    
    try {
      const response = await api.get<any>('/me/autorizacoes');
      const data = response.data;
      
      if (!Array.isArray(data)) {
        // Armazenar array vazio no cache
        demandasCache.set('authorization', [], 2 * 60 * 1000);
        return [];
      }
      
      // Armazenar no cache (TTL mais curto para autorizações - 2 minutos)
      demandasCache.set('authorization', data, 2 * 60 * 1000);
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar autorizações:', error);
      // Em caso de erro, retornar array vazio ao invés de propagar o erro
      return [];
    }
  }

  /**
   * Força a atualização de um tipo específico de demanda
   */
  async refreshDemandas(
    type: 'all' | 'received' | 'sent' | 'authorization' | 'waitingAuth' | 'toAssign' | 'inProgress' | 'solved' | 'finished' | 'watching'
  ): Promise<Demanda[]> {
    switch (type) {
      case 'all':
        return this.getDemandas(true);
      
      case 'received':
        return this.getDemandasRecebidas(true);
      
      case 'sent':
        return this.getDemandasEnviadas(true);
      
      case 'authorization': {
        // Busca autorizações pendentes e extrai as demandas
        const autorizacoes = await this.getMinhasAutorizacoes(true);
        return autorizacoes.map((auth: any) => auth.demanda).filter(Boolean);
      }
      
      case 'waitingAuth': {
        // Busca demandas recebidas e filtra por status (compatibilidade com código antigo)
        const receivedDemands = await this.getDemandasRecebidas(true);
        return receivedDemands.filter(d => d.status === 'AGUARDANDOAUTORIZACAO');
      }
      
      case 'toAssign':
        return this.getDemandasEmAtribuicao(true);
      
      case 'inProgress': {
        // Busca demandas recebidas e filtra por status
        const receivedDemands = await this.getDemandasRecebidas(true);
        return receivedDemands.filter(d => d.status === 'EMPROGRESSO');
      }
      
      case 'solved': {
        // Busca demandas recebidas e filtra por status
        const receivedDemands = await this.getDemandasRecebidas(true);
        return receivedDemands.filter(d => d.status === 'SOLUCIONADA');
      }
      
      case 'finished': {
        // Busca demandas recebidas e filtra por status
        const receivedDemands = await this.getDemandasRecebidas(true);
        return receivedDemands.filter(d => d.status === 'FINALIZADA');
      }
      
      case 'watching':
        return this.getDemandasObservaveis(true);
      
      default:
        throw new Error(`Tipo de demanda inválido: ${type}`);
    }
  }

  /**
   * Invalida o cache para um tipo específico
   */
  invalidateCache(
    type?: 'all' | 'received' | 'sent' | 'authorization' | 'waitingAuth' | 'toAssign' | 'inProgress' | 'solved' | 'finished' | 'watching'
  ): void {
    if (type) {
      demandasCache.invalidate(type);
    } else {
      demandasCache.invalidateAll();
    }
  }

  /**
   * Verifica se o cache deve ser atualizado
   */
  shouldRefresh(
    type: 'all' | 'received' | 'sent' | 'authorization' | 'waitingAuth' | 'toAssign' | 'inProgress' | 'solved' | 'finished' | 'watching'
  ): boolean {
    return demandasCache.shouldRefresh(type, 0.8); // Refresh quando 80% do TTL passou
  }

  /**
   * Obtém estatísticas do cache
   */
  getCacheStats() {
    return demandasCache.getStats();
  }

  // Buscar demanda específica
  async getDemanda(id: number): Promise<Demanda> {
    await authService.ensureSanctumInitialized();
    
    try {
      const response = await api.get<any>(`/demandas/${id}`);
      // Se a API retornar um array, pegar o primeiro item
      if (Array.isArray(response.data)) {
        return response.data[0] as Demanda;
      }
      // Se for um objeto direto
      return response.data as Demanda;
    } catch (error) {
      console.error('Erro no getDemanda:', error);
      throw error;
    }
  }

}

export const receivedDemandsService = new ReceivedDemandsService();
